import { strings } from '@/services/translation/strings';
import { Dialog, DialogBackdrop, Transition, TransitionChild } from '@headlessui/react';
import { FC, Fragment, ReactNode, Suspense, useMemo } from 'react';
import { FiX } from 'react-icons/fi';
import { merge } from '../../../helpers/Utility';
import { Button } from '../button/Button';
import { Loader } from '../Loader';
import { Title } from '../text/Title';

export interface ModalAction {
  label?: string;
  callback: () => void;
  dangerous?: boolean;
  loading?: boolean;
  primary?: boolean;
  disabled?: boolean;
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  header?: string;
  children?: ReactNode;
  className?: string;
  cancelLabel?: string;
  customActions?: ModalAction[];
}

export const Modal: FC<ModalProps> = ({ children, open, header, onClose, className, cancelLabel, customActions }) => {
  const modalActions: ModalAction[] = useMemo(() => {
    if (!customActions) {
      return [];
    }

    const actions = [{ label: cancelLabel || strings.common.cancel, callback: onClose }, ...customActions];

    return actions;
  }, [customActions, cancelLabel, onClose]);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" static open={open} className="fixed inset-0 z-[10000000] overflow-y-auto" onClose={onClose}>
        <div className="overflow-hidden">
          <div className="pb-safe flex items-end justify-center text-center sm:block sm:p-0 w-screen">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            </TransitionChild>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                className={merge(
                  'w-screen xl:max-w-xl sm:w-5/6',
                  'bg-primary-std fixed bottom-0 inline-block transform text-left shadow-xl transition-all sm:relative sm:bottom-auto sm:rounded-lg sm:pb-1 sm:align-middle'
                )}
              >
                <div className="px-8 py-6 relative">
                  <Suspense fallback={<Loader className={'h-10 mx-auto'} />}>
                    <div className="space-y-4">
                      {header && <Title className="mb-2 pr-8">{header}</Title>}
                      <div className={merge(className, !header ? 'pt-6' : '')}>{children}</div>
                      {modalActions.length > 0 && (
                        <div className="space-y-2 sm:space-y-0 justify-end sm:flex sm:flex-row gap-2">
                          {modalActions.map(m => (
                            <Button
                              key={m.label}
                              primary={m.primary}
                              danger={m.dangerous}
                              className="w-full sm:w-auto"
                              onClick={m.callback}
                              loading={m.loading}
                              disabled={m.disabled}
                            >
                              {m.label || strings.common.submit}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </Suspense>
                  <button className="absolute top-6 right-8" onClick={onClose}>
                    <FiX className="h-6 w-6 sm:h-8 sm:w-8" />
                  </button>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
